// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\rapports\index.js

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { 
  Card, Col, Row, Table, DatePicker, Button, Spin, Empty, 
  message, Modal, List, Tag, Typography, Space, Tooltip,
  Collapse, Badge 
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import Filter from "../../components/filter/Filter";
import { getRapports } from "../../redux/slices/rapports/rapportsThunk";
import { columns } from "./columns";
import { getGovernments } from "../../redux/slices/governments/governmentsThunk";
import { getRegions } from "../../redux/slices/regions/regionsThunk"; 
import { getSpecialtiesForFilter } from "../../redux/slices/specialtys/specialtysThunk";
import { getDelegatesList } from "../../redux/slices/delegates/delegatesThunk";
import { getAllDoctorsForFilter } from "../../redux/slices/persons/personsThunk";
import { RoleEnum } from "../../_utils/roleEnum";
import { 
  DownloadOutlined, AuditOutlined, UserOutlined, 
  RocketOutlined, EnvironmentOutlined, StarFilled,
  CrownOutlined,
  WarningOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import { exportReports } from "../../services/reportService";
import axiosInstance from "../../services/axiosServicesVisit";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const RapportsPage = () => {
  const dispatch = useDispatch();
  
  // --- DONNÉES UTILISATEUR ET PERMISSIONS ---
  const user = useSelector((state) => state.signin.user);
  const isSupervisor = user?.role?.id === RoleEnum.SUPERVISOR;

  // --- ÉTATS LOCAUX ---
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10 });
  const [filters, setFilters] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [newDate, setNewDate] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  // États pour la modale Historique Médecin
  const [doctorModalVisible, setDoctorModalVisible] = useState(false);
  const [doctorDetailData, setDoctorDetailData] = useState([]);
  const [doctorLoading, setDoctorLoading] = useState(false);

  // --- RÉCUPÉRATION DONNÉES REDUX ---
  const rapportsState = useSelector((state) => state.rapports?.rapports || {});
  const rawRapports = rapportsState.data?.data || [];
  const totalItems = rapportsState.data?.total || 0;

  // =========================================================================
  // ⚡ FILTRAGE : EXCLURE TOUS LES RAPPORTS DE CONTRÔLE SUPERVISEURS ⚡
  // =========================================================================
  const rapports_data = useMemo(() => {
    return rawRapports.filter(record => {
      const isControl = 
        (record.name && record.name.toLowerCase().startsWith('visite de contrôle')) ||
        (record.note && record.note.includes('[VISITE DE CONTRÔLE')) ||
        (record.feedback && record.feedback.includes('[VISITE DE CONTRÔLE'));
      return !isControl; // On garde UNIQUEMENT les visites régulières des délégués
    });
  }, [rawRapports]);
  // =========================================================================
  
  const allDelegatesList = useSelector((state) => state.delegates?.delegatesList?.data?.data || []);
  const doctorsList = useSelector((state) => state.persons?.doctorsForFilter || []);
  const governmentsList = useSelector((state) => state.governments?.governments?.data || []);
  const specialtysList = useSelector((state) => state.specialtys?.specialtiesList || []);
  const regionsRaw = useSelector((state) => state.regions?.regions || {});
  const regionsList = regionsRaw.data || []; 

  // --- INITIALISATION DES FILTRES ---
  useEffect(() => {
    if (user) {
      setFilters({
        search: "", 
        specialtyId: [], 
        governmentId: [], 
        regionId: [], 
        personId: [],
        delegateId: isSupervisor ? user.supervisedDelegateIds || [] : [],
        sampleId: [], 
        type: [], 
        dateInterval: [],
        isPartner: ""
      });
    }
  }, [user, isSupervisor]);

  // --- CHARGEMENT DES RÉFÉRENTIELS ---
  useEffect(() => {
    dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 }));
    dispatch(getGovernments({ page: 1, limit: 100 }));
    dispatch(getRegions({ page: 1, itemsPerPage: 1000 })); 
    dispatch(getSpecialtiesForFilter());
    dispatch(getAllDoctorsForFilter());
  }, [dispatch]);

  // --- RÉCUPÉRATION DES RAPPORTS AVEC CACHE BUSTER ---
  const fetchRapports = useCallback(() => {
    if (!filters) return;
    const { dateInterval, ...apiFilters } = filters;
    const finalFilters = { 
        ...apiFilters, 
        dateInterval: dateInterval?.length === 2 ? dateInterval.join(',') : undefined,
        _t: Date.now()
    };
    dispatch(getRapports({ pageNumber: pagination.currentPage, limit: pagination.pageSize, filter: finalFilters }));
  }, [filters, pagination, dispatch]);

  useEffect(() => { fetchRapports(); }, [fetchRapports]);

  // --- HANDLERS ---
  const handleFilterChange = (value, key) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      if (key === "delegateId" && value.length > 0) {
        newFilters.search = ""; 
      }
      return newFilters;
    });
    setPagination(p => ({ ...p, currentPage: 1 }));
  };

  const handleOpenDoctorHistory = async (personId) => {
    if (!personId) return;
    setDoctorModalVisible(true);
    setDoctorLoading(true);
    try {
      const res = await axiosInstance.get(`/v1/delegate-stocks/analytics/doctor-full-details`, {
        params: { personId, startDate: '2020-01-01', endDate: dayjs().format('YYYY-MM-DD'), _t: Date.now() }
      });
      setDoctorDetailData(res.data || []);
    } catch (e) {
      message.error("Erreur historique");
    } finally {
      setDoctorLoading(false);
    }
  };

  // ⚡ EXPORT CSV DU PARCOURS AVEC LES NOTES / OBSERVATIONS ⚡
  const handleExportDoctorHistory = () => {
    if (doctorDetailData.length === 0) return;
    const docName = doctorDetailData[0].doctorName;
    const headers = ["Date", "Region", "Delegue Visiteur", "Type Visite", "Echantillons", "Produits", "Observations / Note"];
    const rows = doctorDetailData.map(item => {
      const isSupervisor = item.isSupervisor || 
                           (item.note && (item.note.includes('[RAPPORT SUPERVISEUR') || item.note.includes('[VISITE DE CONTRÔLE') || item.note.includes('[SUPERVISION'))) ||
                           (item.name && item.name.includes('[SUPERVISEUR]'));

      return [
        dayjs(item.reportDate).format('DD/MM/YYYY HH:mm'),
        item.regionName || "N/A",
        item.delegateName,
        isSupervisor ? "Superviseur" : "Délégué",
        item.samples?.map(s => `${s.name} (x${s.quantity})`).join(' | '),
        item.products?.join(' | '),
        `"${(item.note || item.comment || '').replace(/"/g, '""')}"`
      ];
    });
    const csvContent = [headers, ...rows].map(e => e.join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Parcours_${docName.replace(/\s+/g, '_')}.csv`;
    link.click();
  };

  const createOptions = (data, labelKeys = ["name"], idKey = "id") => 
    Array.isArray(data) ? data.map(item => ({ 
        id: item[idKey], 
        label: labelKeys.map(key => item[key]).filter(Boolean).join(' '), 
        value: item[idKey] 
    })) : [];

  // --- CONFIGURATION DES FILTRES ---
  const optionsLists = [
    { 
      key: "personId", placeholder: "Médecin / Client", multiple: true, 
      options: createOptions(doctorsList, ["firstname", "lastname"]),
      optionFilterProp: "label" 
    },
    { key: "specialtyId", placeholder: "Spécialités", multiple: true, options: createOptions(specialtysList) },
    { key: "governmentId", placeholder: "Gouvernorat", multiple: true, options: createOptions(governmentsList) },
    { 
      key: "regionId", 
      placeholder: "Région", 
      multiple: true, 
      options: createOptions(regionsList),
      optionFilterProp: "label", 
      showSearch: true 
    },
    { 
      key: "delegateId", 
      placeholder: "Délégué", 
      multiple: true, 
      options: createOptions(allDelegatesList, ["firstname", "lastname"]), 
      optionFilterProp: "label",
      showSearch: true 
    },
    { key: "type", placeholder: "Type", multiple: true, options: [ { id: "Médecin", label: "Médecin", value: "Médecin" }, { id: "Pharmacien", label: "Pharmacien", value: "Pharmacien" }, { id: "Grossiste", label: "Grossiste", value: "Grossiste" }] },
    {
      name: "Partenaire",
      placeholder: "Statut Partenaire",
      key: "isPartner",
      multiple: false,
      options: [
        { label: "Tous", value: "" },
        { label: "⭐ Partenaires", value: "true" },
        { label: "Standard", value: "false" }
      ]
    }
  ];

  const handleUpdateDate = async () => {
    if (!editingReport || !newDate) return;
    setUpdateLoading(true);
    try {
        await axiosInstance.patch(`/v1/reports/${editingReport.id}/date`, { date: newDate.format('YYYY-MM-DD') });
        message.success('Date mise à jour.');
        setIsModalVisible(false);
        fetchRapports();
    } catch (error) { message.error("Erreur mise à jour."); }
    finally { setUpdateLoading(false); }
  };

  const handleExportAll = async () => {
    if (!filters) return;
    setExporting(true);
    try {
      const { dateInterval, ...apiFilters } = filters;
      const finalFilters = { 
          ...apiFilters, 
          dateInterval: dateInterval?.length === 2 ? dateInterval.join(',') : undefined 
      };
      const response = await exportReports(finalFilters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export_rapports_${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success("Exportation réussie.");
    } catch (e) {
      message.error("Erreur exportation");
    } finally {
      setExporting(false);
    }
  };

  if (!user || !filters) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin size="large" /></div>;

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card bordered={false} className="criclebox tablespace mb-24" title="Liste des Rapports Délégués" />
          <Card bordered={false} className="criclebox tablespace mb-24">
            <div style={{ padding: '0 24px 24px' }}>
              <Filter 
                onSearchValue={(val) => handleFilterChange(val, "search")} 
                optionsLists={optionsLists} 
                data={filters} 
                onSelect={(val, key) => handleFilterChange(val, key)} 
              />
              <Row gutter={16} style={{ marginTop: 16 }} justify="space-between">
                <Col><RangePicker value={filters.dateInterval?.length === 2 ? [dayjs(filters.dateInterval[0]), dayjs(filters.dateInterval[1])] : null} onChange={(dates) => handleFilterChange(dates ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')] : [], 'dateInterval')} /></Col>
                <Col>
                <Button icon={<DownloadOutlined />} onClick={handleExportAll} loading={exporting}>Exporter CSV</Button>
                <Button onClick={() => setFilters({search: "", specialtyId: [], governmentId: [], regionId: [], personId: [], delegateId: [], sampleId: [], type: [], dateInterval: [], isPartner: ""})}>Réinitialiser</Button></Col>
              </Row>
            </div>
            <Table
              columns={columns((report) => { setEditingReport(report); setNewDate(dayjs(report.date)); setIsModalVisible(true); }, handleOpenDoctorHistory)}
              dataSource={rapports_data}
              loading={rapportsState.loading}
              rowKey="id"
              rowClassName={(record) => {
                let className = (record.id % 2 === 0 ? 'table-row-light' : 'table-row-dark');
                if (record.person?.isPartner) {
                  className += ' row-partner-gold';
                }
                return className;
              }}
              pagination={{ current: pagination.currentPage, pageSize: pagination.pageSize, total: totalItems, onChange: (p, s) => setPagination({ currentPage: p, pageSize: s }), showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} rapports` }}
              scroll={{ x: 'max-content' }}
            />

            <style>{`
                .row-partner-gold td {
                  background-color: #fffbe6 !important;
                }
                .row-partner-gold:hover td {
                  background-color: #fff1b8 !important;
                }
                .row-partner-gold a {
                  color: #856404 !important;
                }
              `}</style>
          </Card>
        </Col>
      </Row>

      {/* ========================================================================= */}
      {/* ⚡ MODALE PARCOURS DE VISITE DU MÉDECIN AVEC DISTINCTION SUPERVISEUR ⚡ */}
      {/* ========================================================================= */}
      <Modal 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '95%', alignItems: 'center' }}>
            <span><AuditOutlined /> Parcours de visite du Médecin</span>
            <Button 
              icon={<DownloadOutlined />} 
              size="small" 
              type="primary" 
              onClick={handleExportDoctorHistory} 
              disabled={doctorDetailData.length === 0}
            >
              Exporter Historique
            </Button>
          </div>
        } 
        open={doctorModalVisible} 
        onCancel={() => setDoctorModalVisible(false)} 
        footer={null} 
        width={1150}
      >
        <Spin spinning={doctorLoading}>
          {/* 1. EN-TÊTE : FICHE RÉCAPITULATIVE DU MÉDECIN */}
          {doctorDetailData.length > 0 && (
            (() => {
              const firstRecord = doctorDetailData[0]; 
              const isPartner = firstRecord.isPartner === true || firstRecord.isPartner === 1 || firstRecord.isPartner === 'true';
              const notes = firstRecord.partnerNotes; 

              // ⚡ 1. FILTRE : Ne garder que les cycles oubliés à partir de l'année 2026 ⚡
              const cyclesCoverage = (firstRecord.cyclesPlanningCoverage || []).map(cov => ({
                ...cov,
                unassignedCycles: (cov.unassignedCycles || []).filter(c => {
                  if (!c.startDate) return false;
                  return dayjs(c.startDate).year() >= 2026;
                })
              }));

              const delegatesWithUnassigned = cyclesCoverage.filter(cov => (cov.unassignedCycles || []).length > 0);
              const totalOublisCount = delegatesWithUnassigned.reduce((acc, curr) => acc + (curr.unassignedCycles?.length || 0), 0);

              return (
                <div>
                  <div style={{ 
                    marginBottom: 16, 
                    padding: '16px 20px', 
                    borderRadius: '10px', 
                    backgroundColor: isPartner ? '#fffbe6' : '#f0f5ff', 
                    border: isPartner ? '2px solid #fadb14' : '1px solid #adc6ff',
                    boxShadow: isPartner ? '0 4px 12px rgba(250, 219, 20, 0.15)' : 'none'
                  }}>
                    <Row gutter={[16, 16]} align="middle">
                      <Col span={16}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                            <UserOutlined style={{ fontSize: '28px', marginTop: '5px', color: isPartner ? '#856404' : '#1890ff' }} />
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                    <Title level={3} style={{ margin: 0, color: isPartner ? '#856404' : '#002766' }}>
                                        {firstRecord.doctorName}
                                    </Title>
                                    {isPartner && (
                                        <Tag color="gold" style={{ 
                                            fontSize: '13px', 
                                            fontWeight: '900', 
                                            border: '1px solid #d4b106', 
                                            borderRadius: '4px',
                                            padding: '2px 10px',
                                            textTransform: 'uppercase'
                                        }}>
                                            <StarFilled style={{ marginRight: '6px' }} /> MÉDECIN PARTENAIRE
                                        </Tag>
                                    )}
                                </div>

                                <Space wrap size={8} style={{ marginTop: '8px' }}>
                                    <Tag color="blue" style={{ fontWeight: 'bold' }}>{firstRecord.specialtyName}</Tag>
                                    <Text type="secondary"><EnvironmentOutlined /> {firstRecord.regionName || "N/A"}</Text>
                                    {firstRecord.address && <Text type="secondary">({firstRecord.address})</Text>}
                                </Space>
                                
                                {isPartner && notes && (
                                    <div style={{ 
                                        marginTop: '15px', 
                                        padding: '10px 15px', 
                                        background: 'rgba(255, 255, 255, 0.6)', 
                                        borderRadius: '6px', 
                                        borderLeft: '4px solid #fadb14' 
                                    }}>
                                        <Text strong style={{ color: '#856404', display: 'block', marginBottom: '3px' }}>
                                            Historique Partenariat :
                                        </Text>
                                        <Text style={{ color: '#856404', fontSize: '13px' }}>
                                            {notes}
                                        </Text>
                                    </div>
                                )}
                            </div>
                        </div>
                      </Col>

                      <Col span={8} style={{ 
                          textAlign: 'right', 
                          borderLeft: `2px solid ${isPartner ? '#fadb14' : '#adc6ff'}`, 
                          paddingLeft: '20px' 
                      }}>
                        <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '8px' }}>
                          Délégués assignés au portefeuille :
                        </Text>
                        <Space wrap size={[4, 8]}>
                          {firstRecord.assignedDelegates?.length > 0 ? (
                            firstRecord.assignedDelegates.map((name, idx) => (
                              <Tag key={idx} color="cyan" icon={<RocketOutlined />} style={{ borderRadius: '10px' }}>
                                {name.toUpperCase()}
                              </Tag>
                            ))
                          ) : (
                            <Text type="secondary" italic style={{ fontSize: '11px' }}>Aucun délégué assigné</Text>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </div>

                  {/* ⚡ 2. ACCORDÉON DES OUBLIS DE PLANIFICATION PAR CYCLE (>= 2026) ⚡ */}
                  {cyclesCoverage.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <Collapse
                        defaultActiveKey={delegatesWithUnassigned.length > 0 ? ['audit_oubli'] : []}
                        style={{ 
                          background: delegatesWithUnassigned.length > 0 ? '#fff2f0' : '#f6ffed', 
                          border: delegatesWithUnassigned.length > 0 ? '1px solid #ffa39e' : '1px solid #b7eb8f',
                          borderRadius: '8px'
                        }}
                        items={[
                          {
                            key: 'audit_oubli',
                            label: (
                              <Space>
                                {delegatesWithUnassigned.length > 0 ? (
                                  <>
                                    <WarningOutlined style={{ color: '#cf1322', fontSize: '16px' }} />
                                    <span style={{ color: '#cf1322', fontWeight: 'bold', fontSize: '13px' }}>
                                      Alertes Oublis de Planification par Cycle
                                    </span>
                                    <Badge count={`${totalOublisCount} cycle(s) oublié(s)`} style={{ backgroundColor: '#cf1322' }} />
                                  </>
                                ) : (
                                  <>
                                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                                    <span style={{ color: '#389e0d', fontWeight: 'bold', fontSize: '13px' }}>
                                      Planification Optimale : Ce médecin est planifié dans 100% des cycles
                                    </span>
                                    <Tag color="success" style={{ marginLeft: 8 }}>100% Couvert</Tag>
                                  </>
                                )}
                              </Space>
                            ),
                            children: (
                              <div>
                                {delegatesWithUnassigned.length > 0 ? (
                                  delegatesWithUnassigned.map((cov, idx) => {
                                    const unassignedList = cov.unassignedCycles || [];
                                    const assignedList = cov.assignedCycles || [];

                                    return (
                                      <div 
                                        key={idx} 
                                        style={{ 
                                          padding: '10px 14px', 
                                          background: '#ffffff', 
                                          borderRadius: '6px', 
                                          border: '1px solid #ffccc7',
                                          marginBottom: idx !== delegatesWithUnassigned.length - 1 ? 8 : 0 
                                        }}
                                      >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                          <Text strong style={{ color: '#1890ff', fontSize: '13px' }}>
                                            👤 Délégué : {cov.delegateName}
                                          </Text>
                                          <Tag color="default" style={{ fontSize: '11px', color: '#595959' }}>
                                            Planifié dans {assignedList.length} autre(s) cycle(s)
                                          </Tag>
                                        </div>

                                        <div>
                                          <Text strong style={{ fontSize: '12px', color: '#cf1322', display: 'block', marginBottom: 6 }}>
                                            ⚠️ NON ASSIGNÉ dans ces cycles ({unassignedList.length}) :
                                          </Text>
                                          <Space wrap size={[6, 8]}>
                                            {unassignedList.map((c, cIdx) => {
                                              const periodStr = c.startDate && c.endDate 
                                                ? `(${dayjs(c.startDate).format('DD/MM/YY')} au ${dayjs(c.endDate).format('DD/MM/YY')})`
                                                : '';

                                              return (
                                                <Tag 
                                                  key={cIdx} 
                                                  color="error" 
                                                  style={{ 
                                                    fontWeight: 'bold', 
                                                    fontSize: '11px', 
                                                    padding: '3px 10px',
                                                    borderRadius: '4px' 
                                                  }}
                                                >
                                                  ⚠️ {c.cycleName} <span style={{ fontWeight: 'normal', opacity: 0.9, marginLeft: 4 }}>{periodStr}</span>
                                                </Tag>
                                              );
                                            })}
                                          </Space>
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <Text style={{ color: '#389e0d', fontSize: '12px' }}>
                                    Tous les délégués assignés ont bien planifié ce médecin dans l'ensemble de leurs cycles. Aucun oubli détecté.
                                  </Text>
                                )}
                              </div>
                            )
                          }
                        ]}
                      />
                    </div>
                  )}
                </div>
              );
            })()
          )}          
          {/* 2. EN-TÊTE DU TABLEAU DES VISITES (AVEC NOUVELLE COLONNE OBSERVATIONS) */}
          <div style={{ 
            background: '#f5f5f5', 
            padding: '10px 15px', 
            borderRadius: '6px', 
            marginBottom: '10px', 
            display: 'flex', 
            fontWeight: 'bold',
            fontSize: '12px',
            border: '1px solid #e8e8e8'
          }}>
            <div style={{ width: '15%' }}>Date & Heure</div>
            <div style={{ width: '20%' }}>Visiteur</div>
            <div style={{ width: '22%' }}>Échantillons distribués</div>
            <div style={{ width: '18%' }}>Produits présentés</div>
            <div style={{ width: '25%' }}>Observations / Notes</div>
          </div>

          {/* 3. LISTE DES VISITES AVEC IDENTIFICATION DISTINCTIVE SUPERVISEUR */}
          <List 
            dataSource={doctorDetailData} 
            locale={{ emptyText: "Aucune visite trouvée pour ce médecin." }}
            renderItem={item => {
              // Détection si la visite provient d'un superviseur
              const isSupervisor = item.isSupervisor || 
                (item.note && (item.note.includes('[RAPPORT SUPERVISEUR') || item.note.includes('[VISITE DE CONTRÔLE') || item.note.includes('[SUPERVISION'))) ||
                (item.name && (item.name.includes('[SUPERVISEUR]') || item.name.toLowerCase().includes('visite de contrôle')));

              // Nettoyage de la note pour affichage concis
              let displayNote = item.note || item.comment || '';
              if (displayNote.includes('Observations :')) {
                displayNote = displayNote.split('Observations :')[1].trim();
              }

              return (
                <List.Item style={{ 
                  padding: '12px 15px', 
                  marginBottom: '8px',
                  borderRadius: '6px',
                  // ⚡ FOND VIOLET DOUX ET BORDURE VIOLETTE POUR LES SUPERVISEURS ⚡
                  background: isSupervisor ? '#fcf6ff' : '#ffffff', 
                  border: isSupervisor ? '1px solid #d3adf7' : '1px solid #f0f0f0',
                  borderLeft: isSupervisor ? '5px solid #722ed1' : '1px solid #f0f0f0',
                  transition: 'all 0.2s'
                }}>
                  <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    {/* Date & Heure */}
                    <div style={{ width: '15%' }}>
                      <Text strong>{dayjs(item.reportDate).format('DD/MM/YYYY')}</Text>
                      <br/>
                      <Text type="secondary" style={{ fontSize: '11px' }}>{dayjs(item.reportDate).format('HH:mm')}</Text>
                    </div>

                    {/* Visiteur (Badge violet avec couronne 👑 si superviseur) */}
                    <div style={{ width: '20%' }}>
                      {isSupervisor ? (
                        <Tag color="purple" style={{ fontWeight: 'bold', padding: '3px 8px', borderRadius: '4px' }}>
                          <CrownOutlined style={{ marginRight: 5 }} /> {item.delegateName}
                        </Tag>
                      ) : (
                        <Tag icon={<UserOutlined />} color="default">
                          {item.delegateName}
                        </Tag>
                      )}
                    </div>

                    {/* Échantillons */}
                    <div style={{ width: '22%' }}>
                      {item.samples && item.samples.length > 0 ? (
                        item.samples.map((s, i) => (
                          <Tag 
                            key={i} 
                            color={s.isSpecific ? "error" : "processing"} 
                            style={{ marginBottom: '3px', fontSize: '11px' }}
                          >
                            {s.isSpecific && "[HORS CIBLE] "}{s.name} x{s.quantity}
                          </Tag>
                        ))
                      ) : (
                        <Text type="secondary" style={{ fontSize: '11px' }}>Aucun</Text>
                      )}
                    </div>

                    {/* Produits */}
                    <div style={{ width: '18%' }}>
                      {item.products && item.products.length > 0 ? (
                        item.products.map((p, i) => (
                          <Tag key={i} color="orange" style={{ marginBottom: '3px', fontSize: '11px' }}>{p}</Tag>
                        ))
                      ) : (
                        <Text type="secondary" style={{ fontSize: '11px' }}>-</Text>
                      )}
                    </div>

                    {/* ⚡ NOUVELLE COLONNE : OBSERVATIONS / NOTES ⚡ */}
                    <div style={{ width: '25%' }}>
                      {displayNote ? (
                        <Tooltip title={item.note || displayNote}>
                          <div style={{ 
                            fontSize: '12px', 
                            color: isSupervisor ? '#531dab' : '#262626', 
                            background: isSupervisor ? '#f9f0ff' : '#f5f5f5',
                            padding: '5px 10px',
                            borderRadius: '4px',
                            border: isSupervisor ? '1px dashed #d3adf7' : '1px solid #e8e8e8',
                            maxHeight: '48px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer'
                          }}>
                            📝 {displayNote}
                          </div>
                        </Tooltip>
                      ) : (
                        <Text type="secondary" style={{ fontSize: '11px' }}>-</Text>
                      )}
                    </div>
                  </div>
                </List.Item>
              );
            }} 
          />
          {doctorDetailData.length === 0 && !doctorLoading && <Empty description="Aucune visite trouvée pour ce médecin." />}
        </Spin>
      </Modal>

      <Modal title="Modifier la date" open={isModalVisible} onOk={handleUpdateDate} onCancel={() => setIsModalVisible(false)} confirmLoading={updateLoading}>
        <DatePicker value={newDate} onChange={setNewDate} format="YYYY-MM-DD" style={{ width: '100%' }} />
      </Modal>
    </div>
  );
};

export default RapportsPage;